import React from "react";
import { UPDATE_QUOTE_PARAMETERS, updateJobState } from "../redux_store/jobState";
import { SHOW_DESIGNER_MESSAGE } from "../redux_store/jobState_Validation";

const arraysEqual = (a = [], b = []) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

export const ErpEditorInternal = ({ prefix, initialErpNumbers = [], dispatch, onChange }) => {
    const [erpNumbers, setErpNumbers] = React.useState(initialErpNumbers || []);
    const [savedErpNumbers, setSavedErpNumbers] = React.useState(initialErpNumbers || []);
    const [newErp, setNewErp] = React.useState(prefix ?? "");

    // track last value we persisted so when redux updates it we don't treat it as "external change"
    const lastPersistedRef = React.useRef(JSON.stringify(initialErpNumbers || []));

    // Sync only when the contents actually changed and it's not the same value we just persisted.
    React.useEffect(() => {
        const incoming = initialErpNumbers || [];
        const incomingJSON = JSON.stringify(incoming);
        const savedJSON = JSON.stringify(savedErpNumbers || []);

        // If it's exactly what we last persisted, don't treat it as an external override.
        if (incomingJSON === lastPersistedRef.current) {
            // but ensure savedErpNumbers has the same content (reference may differ)
            if (!arraysEqual(savedErpNumbers, incoming)) {
                setSavedErpNumbers(incoming);
            }
            return;
        }

        // IMPORTANT: protect against parent sending an empty array and clobbering local edits.
        // If incoming is empty and the editor currently has local entries that are not what we persisted,
        // we assume those are user edits and we should NOT overwrite them.
        if (incoming.length === 0 && savedErpNumbers && savedErpNumbers.length > 0) {
            // Only accept the empty incoming if it matches the last persisted value (i.e. we intentionally persisted empty)
            if (lastPersistedRef.current === incomingJSON) {
                setErpNumbers(incoming);
                setSavedErpNumbers(incoming);
                lastPersistedRef.current = incomingJSON;
            }
            return;
        }

        // If incoming content actually differs from saved, sync.
        if (!arraysEqual(incoming, savedErpNumbers || [])) {
            setErpNumbers(incoming);
            setSavedErpNumbers(incoming);
            lastPersistedRef.current = incomingJSON;
        }
    }, [initialErpNumbers]); // only respond to changes in the incoming prop

    // keep newErp in sync with prefix changes (but don't clobber user typing)
    React.useEffect(() => {
        setNewErp(prefix ?? "");
    }, [prefix]);

    // Update ERP value
    const handleChange = (idx, value) => {
        const val = (value || "").toUpperCase();

        if (/^[A-Z]{0,3}[0-9]{0,6}$/.test(val)) {
            const updated = [...erpNumbers];
            updated[idx] = val;
            setErpNumbers(updated);

            if (val.length === 9) {
                if (onChange) {
                    onChange(updated);
                } else if (dispatch) {
                    dispatch(updateJobState(UPDATE_QUOTE_PARAMETERS, "ErpNumbers", updated));
                }
                setSavedErpNumbers(updated);
                lastPersistedRef.current = JSON.stringify(updated);
            }
        }

        console.groupEnd();
    };

    // Delete ERP
    const handleDeleteErp = (idxToRemove) => {
        const updated = erpNumbers.filter((_, idx) => idx !== idxToRemove);

        setErpNumbers(updated);
        setSavedErpNumbers(updated);

        if (onChange) {
            onChange(updated);
        } else if (dispatch) {
            dispatch(updateJobState(UPDATE_QUOTE_PARAMETERS, "ErpNumbers", updated, true));
        }

        lastPersistedRef.current = JSON.stringify(updated);
        console.groupEnd();
    };

    // Add new ERP
    const handleAddNewErp = () => {
        if (newErp.length === 9) {
            const updated = [...erpNumbers, newErp];

            setErpNumbers(updated);
            setSavedErpNumbers(updated);
            setNewErp(prefix ?? "");

            if (onChange) onChange(updated);
            else if (dispatch) dispatch(updateJobState(UPDATE_QUOTE_PARAMETERS, "ErpNumbers", updated));

            lastPersistedRef.current = JSON.stringify(updated);
        } else {
            if (dispatch) {
                dispatch({
                    type: SHOW_DESIGNER_MESSAGE,
                    message: {
                        message: "INVALID ERP NUMBER: length of 9 required",
                        type: "error",
                    },
                });
            }
        }

        console.groupEnd();
    };

    // Revert invalid edits
    const handleBlur = (idx) => {
        const current = erpNumbers[idx] ?? "";
        if (current.length !== 9) {
            const reverted = [...erpNumbers];
            reverted[idx] = (savedErpNumbers[idx] ?? "") || "";

            setErpNumbers(reverted);

            if (dispatch) {
                dispatch({
                    type: SHOW_DESIGNER_MESSAGE,
                    message: `ERP number not saved: must be exactly 9 characters`,
                });
            }

            if (onChange) onChange(reverted);
            else if (dispatch) dispatch(updateJobState(UPDATE_QUOTE_PARAMETERS, "ErpNumbers", reverted));
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") handleAddNewErp();
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <strong>Please Enter ERP Order Numbers</strong>

            {(erpNumbers || []).map((erp, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                        maxLength={9}
                        value={erp}
                        onChange={(e) => handleChange(idx, e.target.value)}
                        onBlur={() => handleBlur(idx)}
                    />
                    <button
                        onClick={() => handleDeleteErp(idx)}
                        style={{
                            background: "lightgray",
                            color: "white",
                            border: "none",
                            padding: "4px 8px",
                            cursor: "pointer",
                            borderRadius: "4px",
                        }}
                    >
                        Delete
                    </button>
                </div>
            ))}

            <div style={{ display: "flex", alignItems: "center" }}>
                <input
                    type="text"
                    maxLength={9}
                    value={newErp}
                    defaultValue={prefix ?? ""}
                    onChange={(e) => {
                        const val = (e.target.value || "").toUpperCase();
                        if (/^[A-Z]{0,3}[0-9]{0,6}$/.test(val)) {
                            setNewErp(val);
                        }
                    }}
                    onKeyDown={handleKeyPress}
                />
                <button
                    onClick={handleAddNewErp}
                    style={{
                        marginLeft: "8px",
                        padding: "4px 8px",
                        background: "#3B64AB",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        width: "55px",
                    }}
                >
                    Add
                </button>
            </div>
        </div>
    );
};
